<script setup lang="ts">
    const { headline = '' } = defineProps<{
        headline?: string;
    }>();

    const slots = useSlots();
    const hasTeaser = computed(() => Boolean(slots.teaser));
</script>

<template>
    <section
        class="trainings-carousel"
        :class="{ 'has-teaser': hasTeaser }"
    >
        <div
            v-if="hasTeaser"
            class="carousel-teaser"
        >
            <slot name="teaser" />
        </div>
        <div class="carousel-main">
            <div class="carousel-header">
                <h2
                    v-if="headline"
                    class="carousel-headline"
                >
                    {{ headline }}
                </h2>
            </div>
            <slot />
            <div
                v-if="slots.link"
                class="carousel-link"
            >
                <slot name="link" />
            </div>
        </div>
    </section>
</template>

<style lang="scss" scoped>
    .trainings-carousel {
        --carousel-teaser-width: 31rem;
        --carousel-header-offset: 0px;
        /* horizontal space next to the carousel column, needed for the right-edge bleed math */
        --carousel-siblings-width: 0px;

        position: relative;
        max-width: calc(100svw - (4 * var(--spacing)));
        overflow: hidden;

        margin: var(--spacing--2) var(--spacing) 0 var(--spacing);

        @media (min-width: $br-l) {
            margin: var(--spacing--2) auto 0 auto;
            max-width: var(--fluid-max-width);
        }

        &.has-teaser {
            @media (min-width: $br-l) {
                --carousel-header-offset: var(--spacing--2);
                --carousel-siblings-width: calc(var(--carousel-teaser-width) + var(--spacing--2));

                display: grid;
                grid-template-columns: var(--carousel-teaser-width) minmax(0, 1fr);
                gap: var(--spacing--2);
                align-items: start;
            }
        }
    }

    .carousel-teaser {
        margin-bottom: var(--spacing--2);

        @media (min-width: $br-l) {
            margin-bottom: 0;
        }
    }

    .carousel-main {
        position: relative;
        min-width: 0;
        padding-top: var(--carousel-header-offset);
        /* flex column so the button's align-self based alignment classes work */
        display: flex;
        flex-direction: column;
    }

    .carousel-link {
        margin-top: var(--spacing--2);
    }

    .carousel-header {
        display: flex;
        align-items: center;
        min-height: 3rem;
        margin-bottom: var(--spacing);
    }

    .carousel-headline {
        font-size: var(--font-l);
        font-weight: 700;
        /* keep clear of the absolutely positioned arrows */
        padding-right: 8rem;
        margin: 0;
    }

    :deep(.splide) {
        position: static;
    }

    /* bleed the track to the right viewport edge while the section stays at content width */
    :deep(.carousel-splide) {
        margin-right: calc((100svw - 100% - var(--carousel-siblings-width)) / -2);

        @supports not (width: 100svw) {
            margin-right: calc((100vw - 100% - var(--carousel-siblings-width)) / -2);
        }
    }

    :deep(.splide__arrows) {
        display: none;

        @media (min-width: $br-m) {
            position: absolute;
            top: var(--carousel-header-offset);
            right: 0;
            display: flex;
            gap: var(--spacing--05);
        }
    }

    :deep(.splide__arrow) {
        position: static;
        transform: none;
        width: 44px;
        height: 44px;
        border-radius: var(--border-radius);
        background: var(--black);
        color: var(--white);
        opacity: 1;
        transition: background var(--anim-duration-hover) var(--anim-ease-bounce);

        /* global font.scss sets color on every span, overriding the inherited button color */
        span.iconify {
            color: inherit;
        }
    }

    :deep(.splide__arrow:hover:not(:disabled)) {
        background: var(--brand-primary);
    }

    :deep(.splide__arrow:disabled) {
        opacity: 0.3;
    }

    :deep(.carousel-slide) {
        display: flex;
        justify-content: center;
    }
</style>
